import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import * as Store from '@apply/store';
import DataGrid, { Column, LoadPanel, MasterDetail } from 'devextreme-react/data-grid';
import { RequestDrawModal, BLUE_BACKGROUND_DARK, BLUE_BACKGROUND_LIGHT } from '@apply/ui';
import OutstandingApplicationDrawHistoryGrid from './OutstandingApplicationDrawHistoryGrid';
import { applicationInsights } from '../../app/ApplicationInsights';
import { DrawType } from '@apply/constants';
import { useAuthentication } from '../LoginPage';
import React from 'react';
import { DrawApi } from '@apply/api';

export interface OustandingApplicationGridProps {
    actions?: Actions;
}

export interface Actions {
    getOutstandingDrawGrid?: () => Promise<Store.CrossStore.IOutStandingDrawGridRow[]>;
    addDraw: (
        drawBudgetId: number,
        drawType: Store.CrossStore.DrawType,
        fontId: number | undefined,
        ipAddress: string | undefined
    ) => Promise<Store.CrossStore.IDrawBudget>;
}

const OustandingApplicationGrid: React.FC<OustandingApplicationGridProps> = ({ actions }) => {
    const drawApi = useMemo(() => new DrawApi.DrawApi(applicationInsights), [applicationInsights]);

    const dataGridRef = useMemo(() => React.createRef<DataGrid>(), []);
    const [dataSource, set_dataSource] = useState<Store.CrossStore.IOutStandingDrawGridRow[]>();
    const [modalDrawBudgetId, set_modalDrawBudgetId] = useState<number>();
    const [modalIsOutOfBalance, set_modalIsOutOfBalance] = useState<boolean>(false);
    const [modalIsOutOfBalanceWaived, set_modalIsOutOfBalanceWaived] = useState<boolean>(false);
    const [modalAccountNum, set_modalAccountNum] = useState<number>();
    const [modalPercentFunded, set_modalPercentFunded] = useState<number>();
    const [isDrawModalOpen, set_isDrawModalOpen] = useState<boolean>(false);

    const { authentication } = useAuthentication();
    const borrowerName = useMemo(() => `${authentication?.user?.firstName} ${authentication?.user?.lastName}`, [authentication]);

    useEffect(() => {
        dataGridRef.current?.instance.beginCustomLoading();
        actions.getOutstandingDrawGrid().then((rows) => {
            set_dataSource(rows);
            dataGridRef.current?.instance.endCustomLoading();
        });
    }, [actions]);

    const onActionsRender = (renderEvent) => {
        const {
            drawBudgetId,
            bankAccountLast4Digits,
            canAddDraw,
            isApproved,
            isBudgetMaxed,
            isFunded,
            isEquityMet,
            isOutOfBalance,
            isOutOfBalanceWaived,
            needsBorrowerSignature,
            borrowerSubmitted,
            latestDrawType,
            percentFunded,
        } = renderEvent.data;

        const EnabledViewBudgetButton = () => (
            <button
                className='button gridActionColumnButton'
                onClick={() => redirectToDraw(drawBudgetId)}
                title='View Draw'
                style={{ padding: '0.5em 1em' }}
            >
                View
            </button>
        );

        const DisabledViewBudgetButton = ({ text }: { text: string }) => (
            <button
                style={{ cursor: 'not-allowed' }}
                className='button gridActionColumnButton hollow secondary'
                title={text}
            >
                View
            </button>
        );

        const EnabledRequestDrawButton = () => (
            <button
                className='button gridActionColumnButton'
                onClick={() =>
                    handleRequestDrawButton(
                        isOutOfBalance,
                        isOutOfBalanceWaived,
                        percentFunded,
                        drawBudgetId,
                        bankAccountLast4Digits
                    )
                }
                title='Request Draw'
            >
                Request
            </button>
        );

        const ContinueDrawButton = ({ text }: { text: string }) => (
            <button
                className='button gridActionColumnButton'
                onClick={() => redirectToDraw(drawBudgetId)}
                title={text}
                style={{ width: 125 }}
            >
                Continue
            </button>
        );

        const DisabledRequestDrawButton = ({ text }: { text: string }) => (
            <button
                style={{ cursor: 'not-allowed' }}
                className='button gridActionColumnButton hollow secondary'
                title={text}
            >
                Request
            </button>
        );

        const RequestDrawButton = () => {
            if (canAddDraw) return <EnabledRequestDrawButton />;

            const disableText = [];

            if (!renderEvent.data?.drawBudgetId) return <DisabledRequestDrawButton text={'No budget has been created.'} />;

            if (latestDrawType == DrawType.Manual && (!borrowerSubmitted || needsBorrowerSignature)) {
                return <ContinueDrawButton text={'Continue your Draw Request.'} />;
            }

            if (!isApproved) disableText.push('Budget waiting for approval.');

            if (!isFunded) disableText.push('Current draw is not funded.');

            if (!isEquityMet) disableText.push('Equity requirement not met.');

            if (isBudgetMaxed) disableText.push('Budget is fully funded.');

            if (disableText.length === 0) disableText.push('A new draw has been created.');

            return <DisabledRequestDrawButton text={disableText.join('\n')} />;
        };

        const ViewBudgetButton = () => {
            if (!renderEvent.data?.drawBudgetId) return <DisabledViewBudgetButton text={'No budget has been created.'} />;

            return <EnabledViewBudgetButton />;
        };

        return (
            <>
                {((borrowerSubmitted && !needsBorrowerSignature) || latestDrawType == DrawType.Automatic || !latestDrawType) && (
                    <ViewBudgetButton />
                )}
                <RequestDrawButton />
            </>
        );
    };

    const renderOutstandingApplicationDrawHistory = (e) => {
        return <OutstandingApplicationDrawHistoryGrid loan={e.data} />;
    };

    const handleRequestDrawButton = (
        isOutOfBalance: boolean,
        isOutOfBalanceWaived: boolean,
        percentFunded: number,
        drawBudgetId?: number,
        accountNum?: number
    ) => {
        set_modalDrawBudgetId(drawBudgetId);
        set_modalIsOutOfBalance(isOutOfBalance);
        set_modalIsOutOfBalanceWaived(isOutOfBalanceWaived);
        set_modalAccountNum(accountNum);
        set_modalPercentFunded(percentFunded);
        set_isDrawModalOpen(true);
    };

    const submitDrawRequest = async ({
        drawBudgetId,
        drawType,
        fontId,
        ipAddress,
    }: {
        drawBudgetId: number;
        drawType: DrawType;
        fontId: number | undefined;
        ipAddress: string | undefined;
    }) => {
        drawApi.addDraw({ drawBudgetId, drawType, fontId, ipAddress });

        if (!dataSource) return;
        const dataItem = dataSource.find((d) => d.drawBudgetId === drawBudgetId);

        if (!dataItem) return;
        dataItem.canAddDraw = false;

        if (dataItem.latestDrawType == DrawType.Manual) {
            dataItem.needsBorrowerSignature = true;
        }
    };

    const handleCloseEvent = () => {
        set_isDrawModalOpen(false);
    };

    const redirectToDraw = (drawBudgetId) => {
        window.location.href = `/draw/${drawBudgetId}`;
    };

    const onCellPrepared = (e) => {
        const width = dataGridRef.current?.instance.element().offsetWidth;

        // Checking for the grid width before applying mobile styles (450px = 28.125em)
        if (width && width < 450 && e.rowType === 'header') {
            e.cellElement.style.display = 'none';
        }

        if (e.rowType === 'header' && e.column.name !== 'address') {
            e.cellElement.style.textAlign = 'Center';
        }

        // Setting data-column here to add the column label to each cell in mobile view
        // Exception: the Address field is to not appear in mobile view and is to be left-aligned
        // Using e.rowType !== 'header' to ensure the table header is only hidden in non-draws grids
        e.column.caption && e.column.caption !== 'Address' && e.cellElement.setAttribute('data-column', e.column.caption);
        if (e.column.caption === 'Address' && e.rowType !== 'header') e.cellElement.style.display = 'table-cell';

        if (e.columnIndex > 1 && e.column.name !== 'available') return;

        e.cellElement.style.color = 'black';

        if (e.rowType === 'header') {
            e.cellElement.style.fontWeight = 600;
            e.cellElement.style.backgroundColor = BLUE_BACKGROUND_LIGHT;
        } else {
            if (e.column.class === 'label') e.cellElement.style.display = 'flex';
            e.cellElement.style.backgroundColor = e.isAltRow ? BLUE_BACKGROUND_LIGHT : BLUE_BACKGROUND_DARK;
        }
    };

    return (
        <section className='data-grid-container'>
            <h4>Outstanding Loans</h4>
            <DataGrid
                ref={dataGridRef}
                keyExpr='loanId'
                id='outstandingApplicationsTable'
                dataSource={dataSource}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                remoteOperations={false}
                allowColumnResizing={true}
                allowColumnReordering={false}
                columnResizingMode={'widget'}
                columnMinWidth={20}
                cellHintEnabled={true}
                onCellPrepared={onCellPrepared}
            >
                <LoadPanel enabled />
                <Column
                    cssClass='label'
                    name='address'
                    dataField='address'
                    caption='Address'
                    minWidth={180}
                />
                <Column
                    cssClass='label'
                    dataField='committed'
                    caption='Committed'
                    format={'currency'}
                    width={100}
                />
                <Column
                    cssClass='label'
                    dataField='funded'
                    caption='Funded'
                    format={'currency'}
                    width={100}
                />
                <Column
                    cssClass='label'
                    name='available'
                    dataField='available'
                    caption='Available'
                    format={'currency'}
                    width={100}
                />
                <Column
                    cssClass='select'
                    cellRender={onActionsRender}
                    alignment='center'
                    width={162}
                />
                <MasterDetail
                    enabled={true}
                    render={renderOutstandingApplicationDrawHistory}
                />
            </DataGrid>
            <RequestDrawModal
                drawBudgetId={modalDrawBudgetId}
                currentAccountNumber={modalAccountNum}
                isOutOfBalance={modalIsOutOfBalance}
                isOutOfBalanceWaived={modalIsOutOfBalanceWaived}
                percentFunded={modalPercentFunded}
                open={isDrawModalOpen}
                submitDrawRequest={submitDrawRequest}
                handleCloseEvent={handleCloseEvent}
                applicationInsights={applicationInsights}
                borrowerName={borrowerName}
            />
        </section>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            getOutstandingDrawGrid: () => dispatch(Store.CrossStore.getOutstandingDrawGrid()),
            addDraw: (
                drawBudgetId: number,
                drawType: Store.CrossStore.DrawType,
                fontId: number | undefined,
                ipAddress: string | undefined
            ) => Store.CrossStore.addDraw(drawBudgetId, drawType, fontId, ipAddress),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(OustandingApplicationGrid);
